import React, { useState } from "react"
import { useDispatch } from "react-redux"
import MoneyInputForm from "../../../common/form/moneyinput.component"
import { updateMatrixItem } from "../../../state/product/ProductActions"
import { BILLING_METHOD, MATRIX_ITEM_UPDATE_OPTION, PRICING_STRATEGY } from "../../../state/product/Types"
import { CurrencyPricingDtoAttributes } from "../../../state/product/currencypricing/CurrencyPricing"
import { MatrixItemDto } from "../../../state/product/matrix/matrix-item/MatrixItem"
import BillingMethodCostLabel from "../../billing-method/costlabel.component"
import BillingMethodPriceLabel from "../../billing-method/pricelabel.component"
import BillingMethodPricingBracketLabel from "../../billing-method/pricingbracketslabel.component"

type InputType =
    | typeof MATRIX_ITEM_UPDATE_OPTION.PRICE
    | typeof MATRIX_ITEM_UPDATE_OPTION.COST

const updateTypePriceMapping = {
    [BILLING_METHOD.ONE_TIME]: MATRIX_ITEM_UPDATE_OPTION.PRICE,
    [BILLING_METHOD.MONTHLY]: MATRIX_ITEM_UPDATE_OPTION.MONTHLY_PRICE,
    [BILLING_METHOD.YEARLY]: MATRIX_ITEM_UPDATE_OPTION.YEARLY_PRICE,
}

const updateTypeCostMapping = {
    [BILLING_METHOD.ONE_TIME]: MATRIX_ITEM_UPDATE_OPTION.COST,
    [BILLING_METHOD.MONTHLY]: MATRIX_ITEM_UPDATE_OPTION.MONTHLY_COST,
    [BILLING_METHOD.YEARLY]: MATRIX_ITEM_UPDATE_OPTION.YEARLY_COST,
}

const updateMultiCurrencyValueMapping = {
    [BILLING_METHOD.ONE_TIME]: "oneTimePrice",
    [BILLING_METHOD.MONTHLY]: "monthlyPrice",
    [BILLING_METHOD.YEARLY]: "yearlyPrice",
}

type Props = {
    inputType: InputType
    hideLabel?: boolean
    item: MatrixItemDto
}

function MatrixItemCostPriceComponent(props: Props) {
    const dispatch = useDispatch()
    const [currency, setCurrency] = useState(props.item.attributes.product.attributes.currency)

    const getMoneyInputLabel = (method) => {
        switch (props.inputType) {
        case MATRIX_ITEM_UPDATE_OPTION.COST:
            return <BillingMethodCostLabel
                billingMethod={method}
                pricingStrategy={props.item.attributes.product.attributes.pricingStrategy}
            />
        case MATRIX_ITEM_UPDATE_OPTION.PRICE:
            return <BillingMethodPriceLabel
                billingMethod={method}
                pricingStrategy={props.item.attributes.product.attributes.pricingStrategy}
            />
        }
    }
    const getAmount = (method: BILLING_METHOD) => {
        if (props.item.attributes.product.attributes.currency === currency) {
            return props.item.attributes.product.attributes[getInputValueKey(method)]
        }
        // @ts-ignore todo to update when global state is split and simplified
        const pricing = props.item.attributes.product.attributes.multiCurrencyPricing.find(mc =>
            // @ts-ignore
            mc.attributes?.currency === currency || mc.currency === currency)
        if (pricing) {
            // @ts-ignore todo to update when global state is split and simplified
            return pricing.attributes ? pricing.attributes[updateMultiCurrencyValueMapping[method]] :
                pricing[updateMultiCurrencyValueMapping[method]]
        }
        return "0"
    }

    const getMultiCurrencyDto = (value: string, method: BILLING_METHOD, currency: string): CurrencyPricingDtoAttributes => {
        // @ts-ignore todo to update when global state is split and simplified
        const dto = props.item.attributes.product.attributes.multiCurrencyPricing.find(mc =>
            // @ts-ignore
            mc.attributes?.currency === currency || mc.currency === currency)
        let oneTimePrice = "0"
        let monthlyPrice = "0"
        let yearlyPrice = "0"
        if (dto) {
            // @ts-ignore todo to update when global state is split and simplified
            oneTimePrice = dto.attributes ? dto.attributes.oneTimePrice : dto.oneTimePrice
            // @ts-ignore todo to update when global state is split and simplified
            monthlyPrice = dto.attributes ? dto.attributes.monthlyPrice : dto.monthlyPrice
            // @ts-ignore todo to update when global state is split and simplified
            yearlyPrice = dto.attributes ? dto.attributes.yearlyPrice : dto.yearlyPrice
        }

        return {
            "oneTimePrice": oneTimePrice,
            "monthlyPrice": monthlyPrice,
            "yearlyPrice": yearlyPrice,
            "currency": currency,
            [updateMultiCurrencyValueMapping[method]]: value
        }
    }

    const getInputValueKey = (method): MATRIX_ITEM_UPDATE_OPTION => {
        switch (props.inputType) {
        case MATRIX_ITEM_UPDATE_OPTION.COST:
            return updateTypeCostMapping[method]
        case MATRIX_ITEM_UPDATE_OPTION.PRICE:
            return updateTypePriceMapping[method]
        }
    }
    const getContent = () => {
        switch (props.item.attributes.product.attributes.pricingStrategy) {
        case PRICING_STRATEGY.MULTIPLIER:
        case PRICING_STRATEGY.BRACKET:
            return props.item.attributes.product.attributes.billingMethods.map(method => {
                return <>
                    {getMoneyInputLabel(method)}

                    <MoneyInputForm
                        className={"mb-2"}
                        currency={currency}
                        value={getAmount(method)}
                        inputClassName={"text-right"}
                        multiCurrency={props.inputType === MATRIX_ITEM_UPDATE_OPTION.PRICE}
                        onChange={value => {
                            if (currency === props.item.attributes.product.attributes.currency) {
                                dispatch(updateMatrixItem({
                                    updateType: getInputValueKey(method),
                                    id: props.item.id,
                                    value: value,
                                }))
                            } else {
                                dispatch(updateMatrixItem({
                                    updateType: MATRIX_ITEM_UPDATE_OPTION.MULTI_CURRENCY_PRICING,
                                    id: props.item.id,
                                    value: getMultiCurrencyDto(value, method, currency)
                                }))
                            }
                        }}
                        onCurrencyChange={c => setCurrency(c)}
                    />
                </>
            })
        case PRICING_STRATEGY.CUSTOM_BRACKET:
        case PRICING_STRATEGY.BULK:
            return <BillingMethodPricingBracketLabel
                labelType={props.inputType}
                hideLabel={props.hideLabel}
                billingMethods={props.item.attributes.product.attributes.billingMethods}
                // @ts-ignore todo to update when global state is split and simplified
                pricingBrackets={props.item.attributes.product.attributes.pricingBrackets.map(pb => pb.attributes || pb)}
                pricingStrategy={props.item.attributes.product.attributes.pricingStrategy}
            />
        case PRICING_STRATEGY.CUSTOM_FORMULA:
            return <></> //todo support custom formula
        default:
            return <></>
        }
    }

    return (
        <div>
            {getContent()}
        </div>
    )
}

export default React.memo(MatrixItemCostPriceComponent)


// WEBPACK FOOTER //
// ./src/product/variant/component/MatrixItemCostPriceComponent.tsx